import {
  ColumnMenuPropsOverrides,
  GridColDef,
  GridColumnMenuContainer,
  GridColumnMenuItemProps,
  GridColumnMenuProps,
  GridRowSelectionModel,
} from '@mui/x-data-grid'
import React, { JSXElementConstructor, useEffect, useMemo, useRef, useState } from 'react'
import { com } from '@eidu/form'
import { GridApiCommunity } from '@mui/x-data-grid/internals'
import { NavigateFunction } from 'react-router-dom'
import { Button, CircularProgress, Stack, TextField, Typography } from '@mui/material'
import PaginationModel from '../../util/pagination/PaginationModel'
import EntityWithLabelAndRelated from '../../domain/entity/EntityWithLabelAndRelated'
import EqualityHashMap from '../../util/EqualityHashMap'
import EntityWithLabel from '../../domain/entity/EntityWithLabel'
import DraftFieldValue, { draftFieldValueToFieldValue, fieldValueToString } from '../../domain/entity/DraftFieldValue'
import hasFieldError from '../../domain/entity/hasFieldError'
import { logException } from '../../util/Logging'
import EntityRepository from '../../io/EntityRepository'
import useEntitiesOfType from '../../io/useEntitiesOfType'
import usePermittedActions from '../../io/usePermittedActions'
import getRenderFieldCell, { CellContent, cellContentComparator } from '../misc/getRenderFieldCell'
import getRenderInvitationCell from '../misc/getRenderInvitationCell'
import { useSnackBar } from '../feedback/SnackBarProvider'
import DataGridWithPageSelection from './DataGridWithPageSelection'
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import EntityId = com.eidu.sharedlib.entity.EntityId
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import Field = com.eidu.sharedlib.entity.field.Field
import entityIdFromString = com.eidu.sharedlib.entity.entityIdFromString
import phoneNumberLoginIdentifierKey = com.eidu.sharedlib.entity.phoneNumberLoginIdentifierKey
import emailLoginIdentifierKey = com.eidu.sharedlib.entity.emailLoginIdentifierKey
import PermittedGlobalAction = com.eidu.sharedlib.entity.permission.PermittedGlobalAction
import GlobalAction = com.eidu.sharedlib.entity.permission.GlobalAction
import EntityTypeKind = com.eidu.sharedlib.entity.type.EntityTypeKind
import invitationStatusKey = com.eidu.sharedlib.entity.invitationStatusKey
import EntityFormInput from '../input/EntityFormInput'

const getColumns = (
  entityType: EntityType,
  entities: ReadonlyMap<EntityId, EntityWithLabel | null>,
  relatedTypes: ReadonlyMap<EntityTypeId, EntityType>,
  showAuthenticationInformation: boolean = false,
  entityRepository?: EntityRepository
): readonly GridColDef[] => [
  { field: 'row', headerName: 'Row', disableColumnMenu: true },
  ...entityType.fields.asJsReadonlyArrayView().map(
    ({ id, name, type }): GridColDef => ({
      field: `field-${id.toString()}`,
      headerName: name,
      sortComparator: cellContentComparator,
      renderCell: getRenderFieldCell(type, entities, relatedTypes),
      display: 'flex',
    })
  ),
  ...(showAuthenticationInformation
    ? [
        {
          field: 'phoneNumberLoginIdentifier',
          headerName: 'Access Phone Number',
          disableColumnMenu: true,
          cellClassName: 'light-gray-background',
          headerClassName: 'light-gray-background',
        },
        {
          field: 'emailLoginIdentifier',
          headerName: 'Access Email',
          disableColumnMenu: true,
          cellClassName: 'light-gray-background',
          headerClassName: 'light-gray-background',
        },
        {
          field: 'invitationStatus',
          headerName: 'Invitation',
          disableColumnMenu: true,
          renderCell: getRenderInvitationCell(entityRepository),
          cellClassName: 'light-gray-background',
          headerClassName: 'light-gray-background',
        },
      ]
    : []),
]

const getRows = (
  entities: readonly EntityWithLabelAndRelated[],
  entityType: EntityType,
  paginationModel: PaginationModel,
  showAuthenticationInformation: boolean = false
): [string, CellContent | number][] =>
  entities.map((entity, index) =>
    Object.fromEntries([
      ...entityType.fields.asJsReadonlyArrayView().map(({ id }) => [
        `field-${id.toString()}`,
        {
          value: entity.entity.valuesByFieldId
            .asJsReadonlyMapView()
            .get(id)
            ?.let((it) => fieldValueToString(it)),
        },
      ]),
      ['id', entity.entity.id.toString()],
      ['row', paginationModel.page * paginationModel.pageSize + index + 1],
      ...(showAuthenticationInformation
        ? [
            [
              'phoneNumberLoginIdentifier',
              entity.entity.externalValues.asJsReadonlyMapView().get(phoneNumberLoginIdentifierKey) || '',
            ],
            [
              'emailLoginIdentifier',
              entity.entity.externalValues.asJsReadonlyMapView().get(emailLoginIdentifierKey) || '',
            ],
            ['invitationStatus', entity.entity.externalValues.asJsReadonlyMapView().get(invitationStatusKey) || ''],
          ]
        : []),
    ])
  )

type BulkEditMenuCustomProps = {
  fieldsById: Map<string, Field>
  selectedEntityIds: string[]
  entityRepository: EntityRepository
  entityTypes: ReadonlyMap<EntityTypeId, EntityType>
}
type BulkEditMenuItemCustomProps = {
  field: Field
  selectedEntityIds: string[]
  entityRepository: EntityRepository
  entityTypes: ReadonlyMap<EntityTypeId, EntityType>
}

const BulkEditMenuItem = ({
  field,
  selectedEntityIds,
  entityRepository,
  entityTypes,
  onClick,
}: GridColumnMenuItemProps & BulkEditMenuItemCustomProps) => {
  const { showSnackBar } = useSnackBar()

  const referenceableEntityTypeIds = Array.from(field.validReferenceTypes?.asJsReadonlySetView() ?? [])

  const referenceableEntitiesByTypeId =
    useEntitiesOfType(
      {
        typeIds: referenceableEntityTypeIds,
        allTypes: entityTypes,
      },
      []
    ) ?? new Map()

  const defaultFieldValue = { fieldId: field.id, type: field.type, value: '' }
  const [fieldValue, setFieldValue] = useState<DraftFieldValue>(defaultFieldValue)

  const [isEditing, setIsEditing] = useState(false)

  const hasError = field && hasFieldError(field, fieldValue, referenceableEntitiesByTypeId)

  return (
    field && (
      <Stack padding={1} spacing={1}>
        <Typography variant="h6">Field ID</Typography>
        <Typography color="gray">{field.id.toString()}</Typography>
        <Typography variant="h6">Edit selected items</Typography>
        <EntityFormInput
          key={`column-form-field-${field.id.toString()}`}
          field={field}
          fieldValue={fieldValue}
          entityTypes={entityTypes}
          updateField={setFieldValue}
        />
        <Stack direction="row" justifyContent="flex-end" sx={{ height: '30px' }}>
          <Button
            disabled={!selectedEntityIds.length || hasError || isEditing}
            onClick={async (event) => {
              setIsEditing(true)
              try {
                const fieldValuesById = new Map([[field.id, draftFieldValueToFieldValue(fieldValue)]])
                await entityRepository.modifyEntities(
                  new Map(
                    selectedEntityIds.map((id) => [
                      entityIdFromString(id),
                      {
                        valuesToUpdateByFieldId: fieldValuesById,
                        linksToUpdate: new Map(),
                        externalValuesToUpdate: new Map(),
                      },
                    ])
                  )
                )
                showSnackBar(`Updated ${selectedEntityIds.length} items`, 'success')
              } catch (e) {
                logException(e)
                showSnackBar(`Failed to update ${selectedEntityIds.length} items`, 'error')
              }
              setIsEditing(false)
              onClick(event)
            }}
          >
            {isEditing ? <CircularProgress size="30px" /> : `Apply (${selectedEntityIds.length})`}
          </Button>
        </Stack>
      </Stack>
    )
  )
}

const CustomColumnMenu = ({
  colDef,
  entityTypes,
  fieldsById,
  selectedEntityIds,
  entityRepository,
  ...props
}: GridColumnMenuProps & BulkEditMenuCustomProps) => (
  <GridColumnMenuContainer {...props} colDef={colDef} onWheel={(e) => e.stopPropagation()}>
    <BulkEditMenuItem
      colDef={colDef}
      onClick={props.hideMenu}
      field={fieldsById.get(colDef.field)!}
      selectedEntityIds={selectedEntityIds}
      entityRepository={entityRepository}
      entityTypes={entityTypes}
    />
  </GridColumnMenuContainer>
)

type ListEntityDataGridProps = {
  navigate: NavigateFunction
  entityRepository: EntityRepository
  entities: readonly EntityWithLabelAndRelated[]
  relatedTypes: ReadonlyMap<EntityTypeId, EntityType>
  entityType: EntityType
  paginationModel: PaginationModel
  setPaginationModel: (paginationModel: PaginationModel) => void
  searchQuery: string
  setSearchQuery: (searchQuery: string) => void
  rowCount: number
  loading: boolean
  apiRef?: React.MutableRefObject<GridApiCommunity>
}

const getIdMap = (entities: readonly EntityWithLabel[]): ReadonlyMap<EntityId, EntityWithLabel> =>
  new EqualityHashMap(entities.map((it) => [it.entity.id, it]))

const ListEntityDataGrid = ({
  navigate,
  entityRepository,
  entities,
  relatedTypes,
  entityType,
  paginationModel,
  setPaginationModel,
  searchQuery,
  setSearchQuery,
  rowCount,
  loading,
  apiRef,
}: ListEntityDataGridProps) => {
  const requested = useRef([new PermittedGlobalAction(GlobalAction.ManageAuthentication)])
  const [permissions] = usePermittedActions(requested.current)

  const hasManageAuthenticationPermission = () =>
    permissions?.some((permission) => permission.equals(new PermittedGlobalAction(GlobalAction.ManageAuthentication)))

  const allEntities = entities.map((it) => [it, ...it.referencedEntities]).flat()
  const columns = useMemo(
    () =>
      getColumns(
        entityType,
        getIdMap(allEntities),
        relatedTypes,
        entityType.kind === EntityTypeKind.User && hasManageAuthenticationPermission(),
        entityRepository
      ),
    [entityType, allEntities, relatedTypes, entityRepository]
  )

  const rows = useMemo(
    () =>
      getRows(
        entities,
        entityType,
        paginationModel,
        entityType.kind === EntityTypeKind.User && hasManageAuthenticationPermission()
      ),
    [entities, entityType, paginationModel]
  )

  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([])
  useEffect(() => {
    setSelectionModel([])
  }, [entityType.id, paginationModel.page])

  const fieldsById = new Map(
    entityType.fields.asJsReadonlyArrayView().map((field) => [`field-${field.id.toString()}`, field])
  )

  const entityTypes = new EqualityHashMap([[entityType.id, entityType], ...relatedTypes.entries()])

  return (
    <Stack spacing={1}>
      <TextField
        label="Search"
        fullWidth
        value={searchQuery}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          setSearchQuery(event.target.value)
        }}
      />
      <DataGridWithPageSelection
        columns={columns}
        rows={rows}
        checkboxSelection
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        paginationMode="server"
        rowCount={rowCount}
        loading={loading}
        apiRef={apiRef}
        onRowClick={(params) => {
          const id = entityIdFromString(params.row.id)
          if (id) navigate(`/entities/${entityType.id.toString()}/view/${id.toString()}`)
        }}
        rowSelectionModel={selectionModel}
        onRowSelectionModelChange={setSelectionModel}
        customColumnMenu={CustomColumnMenu as JSXElementConstructor<GridColumnMenuProps & ColumnMenuPropsOverrides>}
        columnMenuProps={
          { entityTypes, fieldsById, selectedEntityIds: selectionModel, entityRepository } as Partial<
            GridColumnMenuProps & ColumnMenuPropsOverrides
          >
        }
      />
    </Stack>
  )
}

export default ListEntityDataGrid
